import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import apiClient from "../../lib/api";
import { API } from "../../lib/api";
import { useI18n } from "../../i18n";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Upload, Download } from "lucide-react";

const STATUS_STYLES = {
  active: "bg-olive/15 text-olive",
  draft: "bg-secondary-warm text-ink",
  hidden: "bg-black/10 text-ink-muted",
};

const AdminProducts = () => {
  const { lang } = useI18n();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    const params = {};
    if (statusFilter !== "all") params.status = statusFilter;
    if (q) params.q = q;
    return apiClient.get("/admin/products", { params }).then((r) => setItems(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, q]);

  const remove = async (id) => {
    if (!window.confirm(lang === "nl" ? "Product verwijderen?" : "Delete product?")) return;
    try {
      await apiClient.delete(`/products/${id}`);
      toast.success(lang === "nl" ? "Verwijderd" : "Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("auth_token");
    try {
      const res = await fetch(`${API}/admin/products/export.csv`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "snapshop-products.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Exported");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await apiClient.post("/admin/products/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Created ${data.created}, updated ${data.updated}, skipped ${data.skipped}`, { duration: 8000 });
      if (data.errors?.length) console.warn("Import errors:", data.errors);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tighter font-light">{lang === "nl" ? "Producten" : "Products"}</h1>
          <div className="font-label text-ink-muted mt-1">{items.length} {lang === "nl" ? "items" : "items"}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input type="file" accept=".csv" hidden ref={fileRef} onChange={importCsv} data-testid="admin-products-import-input" />
          <button onClick={() => fileRef.current?.click()} disabled={busy} className="btn-secondary text-xs py-2.5 px-3" data-testid="admin-products-import">
            <Upload className="w-3.5 h-3.5" /> {busy ? "…" : "Import CSV"}
          </button>
          <button onClick={exportCsv} className="btn-secondary text-xs py-2.5 px-3" data-testid="admin-products-export">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          <Link to="/admin/products/new" data-testid="admin-product-new" className="btn-primary text-sm">
            <Plus className="w-4 h-4" /> {lang === "nl" ? "Nieuw product" : "New product"}
          </Link>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center bg-white border border-black/15 px-3 py-2">
          <Search className="w-4 h-4 text-ink-muted" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={lang === "nl" ? "Zoeken…" : "Search…"} className="bg-transparent outline-none px-2 text-sm w-56" data-testid="admin-products-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-black/15 bg-white px-3 py-2 text-sm" data-testid="admin-products-status-filter">
          <option value="all">{lang === "nl" ? "Alle statussen" : "All statuses"}</option>
          <option value="active">Active</option>
          <option value="draft">Draft</option>
          <option value="hidden">Hidden</option>
        </select>
      </div>

      <div className="border border-black/10 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary-warm text-left">
            <tr>
              <th className="px-4 py-3 font-label">Image</th>
              <th className="px-4 py-3 font-label">{lang === "nl" ? "Naam" : "Name"}</th>
              <th className="px-4 py-3 font-label">SKU</th>
              <th className="px-4 py-3 font-label">{lang === "nl" ? "Categorie" : "Category"}</th>
              <th className="px-4 py-3 font-label">{lang === "nl" ? "Prijs" : "Price"}</th>
              <th className="px-4 py-3 font-label">{lang === "nl" ? "Voorraad" : "Stock"}</th>
              <th className="px-4 py-3 font-label">Status</th>
              <th className="px-4 py-3 font-label text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {items.map((p) => (
              <tr
                key={p.product_id}
                data-testid={`admin-product-row-${p.sku}`}
                onClick={() => { window.location.href = `/admin/products/${p.product_id}`; }}
                className="hover:bg-secondary-warm/40 cursor-pointer"
              >
                <td className="px-4 py-2"><div className="w-12 h-14 bg-secondary-warm"><img src={p.images?.[0]} alt="" className="w-full h-full object-cover" /></div></td>
                <td className="px-4 py-2"><div className="font-display">{p.name_en}</div><div className="text-xs text-ink-muted">{p.name_nl}</div></td>
                <td className="px-4 py-2 font-mono text-xs">{p.sku}</td>
                <td className="px-4 py-2 text-xs">{p.category}</td>
                <td className="px-4 py-2 font-display">€{p.price.toFixed(2)}</td>
                <td className="px-4 py-2"><span className={p.stock < 5 ? "text-terracotta" : ""}>{p.stock}</span></td>
                <td className="px-4 py-2"><span className={`font-label px-2 py-1 text-[10px] ${STATUS_STYLES[p.status || "active"] || ""}`}>{p.status || "active"}</span></td>
                <td className="px-4 py-2 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-end gap-1">
                    <Link to={`/admin/products/${p.product_id}`} data-testid={`admin-product-edit-${p.sku}`} className="p-2 hover:bg-secondary-warm" title="Edit"><Pencil className="w-4 h-4" /></Link>
                    <button onClick={() => remove(p.product_id)} data-testid={`admin-product-delete-${p.sku}`} className="p-2 hover:bg-terracotta/10 text-terracotta" title="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {items.length === 0 && <div className="text-center text-ink-muted py-10">{lang === "nl" ? "Geen producten." : "No products."}</div>}
    </div>
  );
};

export default AdminProducts;
